import { Workspace, CreateWorkspaceRequest, UpdateWorkspaceRequest, WorkspaceStats, PaginatedResponse } from '../types';
import { apiClient, API_ENDPOINTS, handleApiError, PaginatedRequest } from './api';

export const workspacesService = {
  async getWorkspaces(params: PaginatedRequest = {}): Promise<PaginatedResponse<Workspace>> {
    try {
      console.log('Fetching workspaces from backend API...');
      
      const response = await apiClient.get(API_ENDPOINTS.WORKSPACES.LIST, {
        params: {
          page: params.page || 1,
          per_page: params.per_page || 20,
          search: params.search,
          is_active: true // Only get active workspaces
        }
      });
      
      return {
        items: response.data.workspaces,
        total: response.data.total,
        page: response.data.page,
        size: response.data.per_page,
        pages: response.data.pages
      };
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async getWorkspace(id: string): Promise<Workspace> {
    try {
      console.log(`Fetching workspace ${id} from backend API...`);
      
      const response = await apiClient.get(API_ENDPOINTS.WORKSPACES.GET(id));
      
      return response.data;
    } catch (error) {
      console.error('Error fetching workspace:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async createWorkspace(data: CreateWorkspaceRequest): Promise<Workspace> {
    try {
      console.log('Creating workspace with backend API...', data);
      
      const response = await apiClient.post(API_ENDPOINTS.WORKSPACES.CREATE, {
        name: data.name,
        description: data.description,
        settings: data.settings || {
          default_model: 'gpt-4o-mini',
          max_agents: 100,
          max_workflows: 50,
          allow_external_integrations: true,
          theme: 'light',
          timezone: 'UTC',
          auto_save: true,
          collaboration_enabled: false
        }
      });
      
      console.log('Workspace created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating workspace:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async updateWorkspace(id: string, data: UpdateWorkspaceRequest): Promise<Workspace> {
    try {
      console.log(`Updating workspace ${id} with backend API...`, data);
      
      const response = await apiClient.put(API_ENDPOINTS.WORKSPACES.UPDATE(id), data);
      
      console.log('Workspace updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating workspace:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async deleteWorkspace(id: string): Promise<void> {
    try {
      console.log(`Deleting workspace ${id} with backend API...`);
      
      await apiClient.delete(API_ENDPOINTS.WORKSPACES.DELETE(id));
      
      console.log('Workspace deleted successfully');
    } catch (error) {
      console.error('Error deleting workspace:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async getWorkspaceStats(id: string): Promise<WorkspaceStats> {
    try {
      console.log(`Fetching workspace stats for ${id} from backend API...`);
      
      const response = await apiClient.get(API_ENDPOINTS.WORKSPACES.STATS(id));
      
      // Transform backend response to match frontend interface
      const stats = response.data;
      return {
        total_agents: stats.agent_count || 0,
        total_workflows: stats.workflow_count || 0,
        active_agents: stats.agent_count || 0,
        active_workflows: stats.workflow_count || 0,
        recent_activity: [] // TODO: Implement recent activity from backend
      };
    } catch (error) {
      console.error('Error fetching workspace stats:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async searchWorkspaces(query: string): Promise<Workspace[]> {
    try {
      console.log(`Searching workspaces with query: ${query}`);
      
      const response = await apiClient.get(API_ENDPOINTS.WORKSPACES.LIST, {
        params: {
          search: query,
          per_page: 50,
          is_active: true
        }
      });
      
      return response.data.workspaces;
    } catch (error) {
      console.error('Error searching workspaces:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async switchWorkspace(id: string): Promise<{ workspace_id: string; message: string }> {
    try {
      console.log(`Switching to workspace ${id}...`);
      
      // First verify the workspace exists and user has access
      const workspace = await this.getWorkspace(id);
      
      // Store the current workspace ID in localStorage
      this.setCurrentWorkspaceId(id);
      
      return {
        workspace_id: id,
        message: `Switched to workspace "${workspace.name}"`
      };
    } catch (error) {
      console.error('Error switching workspace:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async duplicateWorkspace(id: string): Promise<Workspace> {
    try {
      console.log(`Duplicating workspace ${id}...`);
      
      const response = await apiClient.post(API_ENDPOINTS.WORKSPACES.DUPLICATE(id));
      
      console.log('Workspace duplicated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error duplicating workspace:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  // Helper methods for workspace management
  getCurrentWorkspaceId(): string | null {
    return localStorage.getItem('current_workspace_id');
  },

  setCurrentWorkspaceId(id: string): void {
    localStorage.setItem('current_workspace_id', id);
  },

  clearCurrentWorkspaceId(): void {
    localStorage.removeItem('current_workspace_id');
  },

  // Get workspace by current user context
  async getCurrentWorkspace(): Promise<Workspace | null> {
    const currentId = this.getCurrentWorkspaceId();
    if (!currentId) {
      return null;
    }
    
    try {
      return await this.getWorkspace(currentId);
    } catch (error) {
      console.error('Error getting current workspace:', error);
      // Clear invalid workspace ID
      this.clearCurrentWorkspaceId();
      return null;
    }
  },

  // Initialize workspace context for new users
  async initializeWorkspaceContext(): Promise<Workspace | null> {
    try {
      // Get user's workspaces
      const workspaces = await this.getWorkspaces({ page: 1, per_page: 1 });
      
      if (workspaces.items.length > 0) {
        const firstWorkspace = workspaces.items[0];
        this.setCurrentWorkspaceId(firstWorkspace.id);
        return firstWorkspace;
      }
      
      return null;
    } catch (error) {
      console.error('Error initializing workspace context:', error);
      return null;
    }
  }
}; 